import React from "react";
import { Check, Clock, Car, Disc3, Gem, Plus, Droplet, Crown } from "lucide-react";

const services = [
  {
    id: "basic-wash",
    name: "Basic Wash",
    tagline: "A fresh, clean finish.",
    duration: "1 – 2 hours",
    price: "$150",
    Icon: Droplet,
    image: "https://images.unsplash.com/photo-1607860108855-64acf2078ed9",
    items: [
      "Exterior Hand Wash",
      "Tire & Wheel Cleaning",
      "Glass Cleaning",
      "Hand Dry & Finish",
    ],
  },
  {
    id: "interior-exterior",
    name: "Interior + Exterior Detail",
    tagline: "A deep clean, inside & out.",
    duration: "3 – 5 hours",
    price: "$350",
    Icon: Car,
    image: "https://images.unsplash.com/photo-1595925889916-2a1d773a0613",
    items: [
      "Exterior Hand Wash",
      "Tire & Wheel Cleaning",
      "Interior Deep Vacuum",
      "Dashboard & Console Wipe",
      "Leather Cleaning & Conditioning",
      "Glass Cleaning",
      "Door Jambs Cleaned",
    ],
  },
  {
    id: "buffing",
    name: "Buffing Package",
    tagline: "Restore. Refresh. Revive.",
    duration: "5 – 7 hours",
    price: "$599",
    addOn: "Add Interior Detail + $200",
    Icon: Disc3,
    image: "https://images.unsplash.com/photo-1780568239721-9922fabf2fed",
    items: [
      "Exterior Hand Wash",
      "Clay Bar Treatment",
      "Single Stage Paint Buff",
      "Tire & Wheel Cleaning",
      "Exterior Protection (Sealant)",
    ],
  },
  {
    id: "ceramic",
    name: "Ceramic Coating + Buff + Clay Bar",
    tagline: "Ultimate protection. Long lasting shine.",
    duration: "7 – 10 hours",
    price: "$1250",
    addOn: "Add Interior Detail + $200",
    Icon: Gem,
    image: "https://images.unsplash.com/photo-1552656967-7a0991a13906",
    items: [
      "Exterior Hand Wash",
      "Clay Bar Treatment",
      "Multi Stage Paint Correction",
      "Ceramic Coating (Paint)",
      "Ceramic Coating (Wheels)",
      "Glass Coating",
      "Exterior Protection",
    ],
  },
  {
    id: "ceramic-9yr",
    name: "9-Year Ceramic Coating",
    tagline: "Nine years of protection. Lifetime shine.",
    duration: "10 – 14 hours",
    price: "$2000",
    addOn: "Add Interior Detail + $200",
    badge: "Flagship",
    Icon: Crown,
    image: "https://images.unsplash.com/photo-1780568239721-9922fabf2fed",
    items: [
      "Exterior Hand Wash",
      "Clay Bar Treatment",
      "Multi Stage Paint Correction",
      "9-Year Ceramic Coating (Paint)",
      "Ceramic Coating (Wheels)",
      "Glass Coating",
      "Trim & Plastic Protection",
      "9-Year Manufacturer Warranty",
    ],
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="section bg-[color:var(--bg)]"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col gap-6 lg:grid lg:grid-cols-12 lg:items-end mb-12">
          <div className="col-span-12 lg:col-span-7">
            <div className="flex items-center gap-3 mb-6 reveal">
              <span className="diamond-divider" />
              <span className="overline">Price Board</span>
            </div>
            <h2 className="reveal font-display tracking-tight text-neutral-900 text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
              Five packages.
              <br />
              <span className="text-neutral-500 font-light italic">
                One uncompromising standard.
              </span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5 reveal">
            <p className="text-neutral-600 leading-relaxed">
              Transparent pricing, performed by experienced detailers with
              professional-grade equipment. Final price may vary based on
              vehicle size and condition. A $50 travel fee may apply for jobs
              beyond a 30km radius.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200">
          {services.map((s) => (
            <article
              key={s.id}
              data-testid={`service-card-${s.id}`}
              className={`relative bg-white p-8 group hover:bg-neutral-50 transition-colors reveal flex flex-col ${
                s.id === "ceramic-9yr" ? "ring-1 ring-[color:var(--accent)]/60" : ""
              }`}
            >
              <div className="aspect-[16/10] overflow-hidden mb-6 -mx-8 -mt-8">
                <img
                  src={s.image}
                  alt={s.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              </div>

              <div className="flex items-center gap-2 text-[color:var(--accent)]">
                <s.Icon size={18} strokeWidth={1.5} />
                <span className="overline text-[color:var(--accent)]">
                  {s.tagline}
                </span>
              </div>

              <h3 className="font-display text-neutral-900 text-2xl md:text-[26px] font-semibold mt-3 leading-tight">
                {s.name}
              </h3>

              <div className="flex items-center gap-2 mt-3 text-neutral-500 text-sm">
                <Clock size={14} className="text-[color:var(--accent)]" />
                <span>{s.duration}</span>
              </div>

              <ul className="mt-5 space-y-2 flex-1">
                {s.items.map((i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2.5 text-sm text-neutral-700"
                  >
                    <Check
                      size={14}
                      className="text-[color:var(--accent)] mt-1 shrink-0"
                    />
                    <span>{i}</span>
                  </li>
                ))}
              </ul>

              {s.addOn && (
                <div className="mt-5 flex items-center gap-2 border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs text-neutral-700">
                  <Plus size={13} className="text-[color:var(--accent)]" />
                  {s.addOn}
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-neutral-200">
                <div className="overline">Starting at</div>
                <div className="flex items-end justify-between mt-1">
                  <div className="font-display text-neutral-900 text-4xl font-bold tracking-tight">
                    {s.price}
                  </div>
                  <a
                    href="#book"
                    data-testid={`service-book-${s.id}`}
                    className="text-[color:var(--accent)] text-sm font-medium hover:text-neutral-900 transition-colors"
                  >
                    Book this →
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
